import type { GeneratePreset } from '@/lib/preset-types'

const DB_NAME = 'byokapp-presets'
const DB_VERSION = 1
const STORE_PRESETS = 'presets'
const STORE_THUMBNAILS = 'thumbnails'

interface ThumbnailRecord {
  id: string
  blob: Blob
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_PRESETS, { keyPath: 'id' })
      req.result.createObjectStore(STORE_THUMBNAILS, { keyPath: 'id' })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

export function getAllPresets(db: IDBDatabase): Promise<GeneratePreset[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PRESETS, 'readonly')
    const req = tx.objectStore(STORE_PRESETS).getAll()
    req.onsuccess = () => resolve(req.result as GeneratePreset[])
    req.onerror = () => reject(req.error)
  })
}

export function putPreset(db: IDBDatabase, preset: GeneratePreset): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PRESETS, 'readwrite')
    const req = tx.objectStore(STORE_PRESETS).put(preset)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

export function deletePreset(db: IDBDatabase, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PRESETS, 'readwrite')
    const req = tx.objectStore(STORE_PRESETS).delete(id)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

export function putThumbnail(db: IDBDatabase, id: string, blob: Blob): Promise<void> {
  return new Promise((resolve, reject) => {
    const record: ThumbnailRecord = { id, blob }
    const tx = db.transaction(STORE_THUMBNAILS, 'readwrite')
    const req = tx.objectStore(STORE_THUMBNAILS).put(record)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

export function getThumbnail(db: IDBDatabase, id: string): Promise<Blob | null> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_THUMBNAILS, 'readonly')
    const req = tx.objectStore(STORE_THUMBNAILS).get(id)
    req.onsuccess = () => {
      const record = req.result as ThumbnailRecord | undefined
      resolve(record?.blob ?? null)
    }
    req.onerror = () => reject(req.error)
  })
}

export function deleteThumbnail(db: IDBDatabase, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_THUMBNAILS, 'readwrite')
    const req = tx.objectStore(STORE_THUMBNAILS).delete(id)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

export async function captureThumbnail(url: string): Promise<Blob | null> {
  if (!url.startsWith('blob:') && !url.startsWith('data:image/')) return null
  try {
    const img = new Image()
    await new Promise<void>((resolve, reject) => {
      // Handlers must be set BEFORE src — data: URLs can fire onload synchronously.
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('load failed'))
      img.src = url
    })
    const SIZE = 180
    const scale = Math.min(SIZE / img.width, SIZE / img.height)
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(img.width * scale)
    canvas.height = Math.round(img.height * scale)
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height)
    return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.65))
  } catch {
    return null
  }
}

export { openDb }
